/**
 * Response validation for the connection contract. Every 2xx body is checked
 * before the UI sees it: shape, enums, and numeric strings. Amounts and token
 * counters must be digit strings (`^\d+$`) - a number, a float or a negative
 * is an invalid response, never "rounded". Unknown extra fields are ignored.
 */
import type {
  AiActivation,
  AiWelcomeCredit,
  AiBalance,
  AiBudget,
  AiBudgetScope,
  AiConversion,
  AiEvidence,
  AiChart,
  AiKpi,
  AiExecution,
  AiMoney,
  AiQuote,
  AiRunRecovery,
  AiRunState,
  AiRunStatus,
  AiSettings,
  AiSettlement,
  AiTurn,
  AiUsageResponse,
  AiUsageRow,
  AiUsageTokens,
  AiCreditPackage,
  AiWalletPackagesResponse,
  AiTopupCheckoutResponse,
  AiTopupVerifyResponse,
  AiTopupOrder,
} from './contract.ts';
import { AI_DOMAINS, EXECUTION_STATUSES, SETTLEMENT_STATUSES } from './contract.ts';
import { AiInvalidResponseError } from './errors.ts';

type Rec = Record<string, unknown>;

function fail(path: string): never {
  throw new AiInvalidResponseError(path);
}

function rec(value: unknown, path: string): Rec {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) fail(path);
  return value as Rec;
}

function str(value: unknown, path: string): string {
  if (typeof value !== 'string') fail(path);
  return value;
}

function strOrNull(value: unknown, path: string): string | null {
  return value === null ? null : str(value, path);
}

function bool(value: unknown, path: string): boolean {
  if (typeof value !== 'boolean') fail(path);
  return value;
}

function arr(value: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(value)) fail(path);
  return value;
}

/** Digit string: integer ≥ 0, no sign, no fraction, no exponent. */
export function digits(value: unknown, path: string): string {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) fail(path);
  return value;
}

function digitsOrNull(value: unknown, path: string): string | null {
  return value === null ? null : digits(value, path);
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], path: string): T {
  if (typeof value !== 'string' || !(allowed as readonly string[]).includes(value)) fail(path);
  return value as T;
}

export function parseMoney(value: unknown, path: string): AiMoney {
  const o = rec(value, path);
  return {
    microCredits: digits(o['microCredits'], `${path}.microCredits`),
    toman: digitsOrNull(o['toman'], `${path}.toman`),
    suppressedBelowOneToman: bool(o['suppressedBelowOneToman'], `${path}.suppressedBelowOneToman`),
  };
}

function parseConversion(value: unknown, path: string): AiConversion | null {
  if (value === null) return null;
  const o = rec(value, path);
  const per = digits(o['microCreditsPerToman'], `${path}.microCreditsPerToman`);
  if (BigInt(per) === 0n) fail(`${path}.microCreditsPerToman`);
  return {
    microCreditsPerToman: per,
    version: str(o['version'], `${path}.version`),
    rounding: oneOf(o['rounding'], ['half_up'] as const, `${path}.rounding`),
  };
}

export function parseBalance(value: unknown): AiBalance {
  const o = rec(value, 'balance');
  const balance: AiBalance = {
    unit: oneOf(o['unit'], ['toman'] as const, 'balance.unit'),
    conversion: parseConversion(o['conversion'], 'balance.conversion'),
    available: parseMoney(o['available'], 'balance.available'),
    reserved: parseMoney(o['reserved'], 'balance.reserved'),
    amountToman: digitsOrNull(o['amountToman'], 'balance.amountToman'),
    amountLabel: strOrNull(o['amountLabel'], 'balance.amountLabel'),
  };
  if (o['rawTokens'] === undefined) return balance;
  if (o['rawTokens'] === null) return { ...balance, rawTokens: null };
  const raw = rec(o['rawTokens'], 'balance.rawTokens');
  const available = digits(raw['available'], 'balance.rawTokens.available');
  const reserved = digits(raw['reserved'], 'balance.rawTokens.reserved');
  const tokensPerToman = digits(raw['tokensPerToman'], 'balance.rawTokens.tokensPerToman');
  const counting = oneOf(
    raw['counting'],
    ['input_plus_output_once'] as const,
    'balance.rawTokens.counting',
  );
  const peg = balance.conversion === null ? 0n : BigInt(balance.conversion.microCreditsPerToman);
  const rate = BigInt(tokensPerToman);
  if (rate !== 5n || peg <= 0n || peg % rate !== 0n) fail('balance.rawTokens.precision');
  const reservedCredits = BigInt(balance.reserved.microCredits);
  if (
    BigInt(available) !== (BigInt(balance.available.microCredits) * rate) / peg ||
    BigInt(reserved) !== (reservedCredits * rate + peg - 1n) / peg
  )
    fail('balance.rawTokens.amount');
  return { ...balance, rawTokens: { available, reserved, tokensPerToman, counting } };
}

const BUDGET_SCOPES: readonly AiBudgetScope[] = ['tenant', 'user', 'feature', 'automation'];

export function parseBudget(value: unknown, path = 'budget'): AiBudget {
  const o = rec(value, path);
  const period = rec(o['period'], `${path}.period`);
  return {
    scope: oneOf(o['scope'], BUDGET_SCOPES, `${path}.scope`),
    scopeKey: str(o['scopeKey'], `${path}.scopeKey`),
    period: {
      start: str(period['start'], `${path}.period.start`),
      end: str(period['end'], `${path}.period.end`),
      zone: str(period['zone'], `${path}.period.zone`),
    },
    hard: parseMoney(o['hard'], `${path}.hard`),
    soft: parseMoney(o['soft'], `${path}.soft`),
    consumed: parseMoney(o['consumed'], `${path}.consumed`),
    openHolds: parseMoney(o['openHolds'], `${path}.openHolds`),
    remaining: parseMoney(o['remaining'], `${path}.remaining`),
    hardCredits: digits(o['hardCredits'], `${path}.hardCredits`),
    softCredits: digits(o['softCredits'], `${path}.softCredits`),
  };
}

/** `GET /budgets` is a JSON array. An object is not this response. */
export function parseBudgets(value: unknown): readonly AiBudget[] {
  return arr(value, 'budgets').map((entry, index) => parseBudget(entry, `budgets[${index}]`));
}

export function parseActivation(value: unknown): AiActivation {
  const o = rec(value, 'activation');
  return {
    active: bool(o['active'], 'activation.active'),
    optedIn: bool(o['optedIn'], 'activation.optedIn'),
    killSwitch: bool(o['killSwitch'], 'activation.killSwitch'),
    tenantActive: bool(o['tenantActive'], 'activation.tenantActive'),
    features: arr(o['features'], 'activation.features').map((entry, i) => {
      const f = rec(entry, `activation.features[${i}]`);
      return {
        code: str(f['code'], `activation.features[${i}].code`),
        implemented: bool(f['implemented'], `activation.features[${i}].implemented`),
        executable: bool(f['executable'], `activation.features[${i}].executable`),
      };
    }),
  };
}

export function parseSettings(value: unknown): AiSettings {
  const o = rec(value, 'settings');
  const days = o['retentionDays'];
  if (typeof days !== 'number' || !Number.isInteger(days) || days < 0)
    fail('settings.retentionDays');
  const version = o['chatModelVersion'];
  if (version !== null && (typeof version !== 'number' || !Number.isInteger(version))) {
    fail('settings.chatModelVersion');
  }
  if (o['importMappingFree'] !== true) fail('settings.importMappingFree');
  if (o['importMappingCustomerChargeToman'] !== '0')
    fail('settings.importMappingCustomerChargeToman');
  return {
    optedIn: bool(o['optedIn'], 'settings.optedIn'),
    retentionDays: days,
    killSwitch: bool(o['killSwitch'], 'settings.killSwitch'),
    chatProviderCode: strOrNull(o['chatProviderCode'], 'settings.chatProviderCode'),
    chatModelCode: strOrNull(o['chatModelCode'], 'settings.chatModelCode'),
    chatModelVersion: version,
    importMappingFree: true,
    importMappingCustomerChargeToman: '0',
  };
}

export function parseQuote(value: unknown): AiQuote {
  const o = rec(value, 'quote');
  if (o['providerCalled'] !== false) fail('quote.providerCalled');
  return {
    quoteId: str(o['quoteId'], 'quote.quoteId'),
    expiresAt: str(o['expiresAt'], 'quote.expiresAt'),
    featureCode: str(o['featureCode'], 'quote.featureCode'),
    providerCode: strOrNull(o['providerCode'], 'quote.providerCode'),
    modelCode: strOrNull(o['modelCode'], 'quote.modelCode'),
    modelVersion: strOrNull(o['modelVersion'], 'quote.modelVersion'),
    dataSufficient: bool(o['dataSufficient'], 'quote.dataSufficient'),
    amountToman: digitsOrNull(o['amountToman'], 'quote.amountToman'),
    amountLabel: strOrNull(o['amountLabel'], 'quote.amountLabel'),
    customerMicroCredits: digits(o['customerMicroCredits'], 'quote.customerMicroCredits'),
    scopeFingerprint: str(o['scopeFingerprint'], 'quote.scopeFingerprint'),
    message: strOrNull(o['message'], 'quote.message'),
    providerCalled: false,
    ...(typeof o['tool'] === 'string' ? { tool: o['tool'] } : {}),
    ...(typeof o['domain'] === 'string' ? { domain: o['domain'] } : {}),
    ...(Number.isInteger(o['outputTokens']) ? { outputTokens: o['outputTokens'] as number } : {}),
    confirmationRequired: o['confirmationRequired'] !== false,
    ...(o['deepSearch'] && typeof o['deepSearch'] === 'object'
      ? {
          deepSearch: {
            enabled: Boolean((o['deepSearch'] as Record<string, unknown>)['enabled']),
            scope: (o['deepSearch'] as Record<string, unknown>)['scope'] === 'web' ||
              (o['deepSearch'] as Record<string, unknown>)['scope'] === 'company'
                ? ((o['deepSearch'] as Record<string, unknown>)['scope'] as 'web' | 'company')
                : 'hybrid',
          },
        }
      : {}),
  };
}

const RUN_STATES: readonly AiRunState[] = ['pending', 'settled', 'failed', 'refunded'];

/** Always an object with four keys; each counter is a digit string or `null`. */
function parseTokens(value: unknown, path: string): AiUsageTokens {
  const o = rec(value, path);
  return {
    inputTokens: digitsOrNull(o['inputTokens'], `${path}.inputTokens`),
    cachedInputTokens: digitsOrNull(o['cachedInputTokens'], `${path}.cachedInputTokens`),
    outputTokens: digitsOrNull(o['outputTokens'], `${path}.outputTokens`),
    reasoningTokens: digitsOrNull(o['reasoningTokens'], `${path}.reasoningTokens`),
  };
}

function parseEvidence(value: unknown, path: string): AiEvidence {
  const o = rec(value, path);
  const label = o['label'];
  if (label !== undefined && typeof label !== 'string') fail(`${path}.label`);
  const name = typeof label === 'string' ? label.trim() : '';
  const unit = o['unit'];
  if (unit !== 'quantity' && unit !== 'rial' && unit !== 'days') fail(`${path}.unit`);
  return {
    entity: str(o['entity'], `${path}.entity`),
    recordId: str(o['recordId'], `${path}.recordId`),
    ...(name === '' ? {} : { label: name }),
    ...(typeof o['onHand'] === 'string' ? { onHand: o['onHand'] } : {}),
    ...(typeof o['amountRial'] === 'string' ? { amountRial: o['amountRial'] } : {}),
    ...(typeof o['openBalanceRial'] === 'string' ? { openBalanceRial: o['openBalanceRial'] } : {}),
    ...(typeof o['projectRef'] === 'string'
      ? { projectRef: o['projectRef'] }
      : o['projectRef'] === null
        ? { projectRef: null }
        : {}),
    ...(o['side'] === 'receivable' || o['side'] === 'payable' ? { side: o['side'] } : {}),
    ...(typeof o['stage'] === 'string'
      ? { stage: o['stage'] }
      : o['stage'] === null
        ? { stage: null }
        : {}),
    ...(typeof o['status'] === 'string' ? { status: o['status'] } : {}),
    ...(typeof o['dueDate'] === 'string' ? { dueDate: o['dueDate'] } : {}),
    ...(typeof o['daysOverdue'] === 'number' ? { daysOverdue: o['daysOverdue'] } : {}),
    unit,
    extractedAt: str(o['extractedAt'], `${path}.extractedAt`),
    source: str(o['source'], `${path}.source`),
    ...(o['group'] !== undefined ? { group: str(o['group'], `${path}.group`) } : {}),
    ...(o['headline'] !== undefined ? { headline: bool(o['headline'], `${path}.headline`) } : {}),
    ...(o['kpi'] !== undefined ? { kpi: parseKpi(o['kpi'], `${path}.kpi`) } : {}),
    ...(o['chart'] !== undefined ? { chart: parseFactChart(o['chart'], `${path}.chart`) } : {}),
  };
}

function parseFactChart(value: unknown, path: string): NonNullable<AiEvidence['chart']> {
  const o = rec(value, path);
  return {
    title: str(o['title'], `${path}.title`),
    label: str(o['label'], `${path}.label`),
    value: finiteChartNumber(o['value'], `${path}.value`),
    unit: str(o['unit'], `${path}.unit`),
  };
}

function parseExecution(value: unknown, path: string): AiExecution {
  const o = rec(value, path);
  return {
    status: oneOf(o['status'], EXECUTION_STATUSES, `${path}.status`),
    providerAttempted: bool(o['providerAttempted'], `${path}.providerAttempted`),
  };
}

/**
 * Settlement invariants of the handoff: `usagePending` is true only for
 * `usage_unknown`; `consumed` / `released` are `null` while the status is
 * `reserved` or `usage_unknown` and money objects after settlement or release.
 */
function parseSettlement(value: unknown, path: string): AiSettlement {
  const o = rec(value, path);
  const status = oneOf(o['status'], SETTLEMENT_STATUSES, `${path}.status`);
  const usagePending = bool(o['usagePending'], `${path}.usagePending`);
  if (usagePending !== (status === 'usage_unknown')) fail(`${path}.usagePending`);
  const open = status === 'reserved' || status === 'usage_unknown';
  const money = (key: string) => (o[key] === null ? null : parseMoney(o[key], `${path}.${key}`));
  const consumed = money('consumed');
  const released = money('released');
  if (open && (consumed !== null || released !== null)) fail(`${path}.consumed`);
  if (!open && (consumed === null || released === null)) fail(`${path}.consumed`);
  return { status, usagePending, held: parseMoney(o['held'], `${path}.held`), consumed, released };
}

function stringArray(value: unknown, path: string): readonly string[] {
  return arr(value, path).map((entry, i) => str(entry, `${path}[${i}]`));
}

function finiteChartNumber(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(path);
  return value;
}

function parseKpi(value: unknown, path: string): AiKpi {
  const o = rec(value, path);
  return {
    label: str(o['label'], `${path}.label`),
    value: str(o['value'], `${path}.value`),
    ...(o['id'] !== undefined ? { id: str(o['id'], `${path}.id`) } : {}),
    ...(o['unit'] !== undefined ? { unit: str(o['unit'], `${path}.unit`) } : {}),
    ...(o['hint'] !== undefined ? { hint: str(o['hint'], `${path}.hint`) } : {}),
  };
}

function parseChart(value: unknown, path: string): AiChart {
  const o = rec(value, path);
  return {
    type: oneOf(
      o['type'],
      ['bar', 'pie', 'line', 'donut', 'metric', 'horizontal-bar'] as const,
      `${path}.type`,
    ),
    title: str(o['title'], `${path}.title`),
    ...(o['unit'] !== undefined ? { unit: str(o['unit'], `${path}.unit`) } : {}),
    ...(Array.isArray(o['categories'])
      ? {
          categories: arr(o['categories'], `${path}.categories`).map((c, i) =>
            str(c, `${path}.categories[${i}]`),
          ),
        }
      : {}),
    ...(Array.isArray(o['series'])
      ? {
          series: arr(o['series'], `${path}.series`).map((s, i) => {
            const item = rec(s, `${path}.series[${i}]`);
            return {
              name: str(item['name'], `${path}.series[${i}].name`),
              data: arr(item['data'], `${path}.series[${i}].data`).map((d, di) =>
                finiteChartNumber(d, `${path}.series[${i}].data[${di}]`),
              ),
            };
          }),
        }
      : {}),
    items: arr(o['items'], `${path}.items`).map((value, index) => {
      const p = `${path}.items[${index}]`;
      const item = rec(value, p);
      return {
        label: str(item['label'], `${p}.label`),
        value: finiteChartNumber(item['value'], `${p}.value`),
        ...(item['unit'] !== undefined ? { unit: str(item['unit'], `${p}.unit`) } : {}),
        ...(item['formattedValue'] !== undefined
          ? { formattedValue: str(item['formattedValue'], `${p}.formattedValue`) }
          : {}),
      };
    }),
  };
}

export function parseTurn(value: unknown): AiTurn {
  const o = rec(value, 'turn');
  const state = oneOf(o['state'], RUN_STATES, 'turn.state');
  const charge = o['charge'] === null ? null : parseMoney(o['charge'], 'turn.charge');
  // A pending turn has no customer charge yet; a finished one has.
  if (state === 'pending' && charge !== null) fail('turn.charge');
  if (state !== 'pending' && charge === null) fail('turn.charge');
  if (o['importMappingFree'] !== true) fail('turn.importMappingFree');
  if (o['importMappingCustomerChargeToman'] !== '0') fail('turn.importMappingCustomerChargeToman');
  return {
    turnId: str(o['turnId'], 'turn.turnId'),
    runId: strOrNull(o['runId'], 'turn.runId'),
    quoteId: strOrNull(o['quoteId'], 'turn.quoteId'),
    correlationId: strOrNull(o['correlationId'], 'turn.correlationId'),
    state,
    stateLabel: str(o['stateLabel'], 'turn.stateLabel'),
    domain: oneOf(o['domain'], AI_DOMAINS, 'turn.domain'),
    tool: str(o['tool'], 'turn.tool'),
    answer: strOrNull(o['answer'], 'turn.answer'),
    evidence: arr(o['evidence'], 'turn.evidence').map((e, i) =>
      parseEvidence(e, `turn.evidence[${i}]`),
    ),
    usage: parseTokens(o['usage'], 'turn.usage'),
    estimate: o['estimate'] === null ? null : parseMoney(o['estimate'], 'turn.estimate'),
    charge,
    amountToman: digitsOrNull(o['amountToman'], 'turn.amountToman'),
    amountLabel: str(o['amountLabel'], 'turn.amountLabel'),
    replayed: bool(o['replayed'], 'turn.replayed'),
    budgetWarnings: stringArray(o['budgetWarnings'], 'turn.budgetWarnings'),
    importMappingFree: true,
    importMappingCustomerChargeToman: '0',
    ...(o['kpis'] !== undefined
      ? { kpis: arr(o['kpis'], 'turn.kpis').map((k, i) => parseKpi(k, `turn.kpis[${i}]`)) }
      : {}),
    ...(o['chart'] !== undefined
      ? { chart: o['chart'] === null ? null : parseChart(o['chart'], 'turn.chart') }
      : {}),
    ...(o['deepSearch'] !== undefined && parseDeepSearch(o['deepSearch'], 'turn.deepSearch')
      ? { deepSearch: parseDeepSearch(o['deepSearch'], 'turn.deepSearch') }
      : {}),
  };
}

function parseDeepSearch(value: unknown, _path: string) {
  if (value === null || typeof value !== 'object') return undefined;
  const o = value as Record<string, unknown>;
  const planRaw = o['plan'] && typeof o['plan'] === 'object' ? (o['plan'] as Record<string, unknown>) : null;
  return {
    ...(planRaw
      ? {
          plan: {
            subQueries: Array.isArray(planRaw['subQueries'])
              ? (planRaw['subQueries'] as unknown[]).filter((q): q is string => typeof q === 'string')
              : [],
            steps: Array.isArray(planRaw['steps'])
              ? (planRaw['steps'] as unknown[]).map((s: any, idx: number) => ({
                  id: typeof s?.id === 'string' ? s.id : `step-${idx}`,
                  query: typeof s?.query === 'string' ? s.query : '',
                  sourceKind:
                    s?.sourceKind === 'web' || s?.sourceKind === 'company' || s?.sourceKind === 'document'
                      ? s.sourceKind
                      : 'web',
                  status:
                    s?.status === 'completed' || s?.status === 'failed' || s?.status === 'running'
                      ? s.status
                      : 'pending',
                  resultCount: Number.isInteger(s?.resultCount) ? s.resultCount : 0,
                  summary: typeof s?.summary === 'string' ? s.summary : undefined,
                }))
              : [],
            status: typeof planRaw['status'] === 'string' ? planRaw['status'] : 'completed',
          },
        }
      : {}),
    ...(Array.isArray(o['sources'])
      ? {
          sources: (o['sources'] as unknown[]).map((s: any, idx: number) => ({
            id: typeof s?.id === 'string' ? s.id : `source-${idx}`,
            kind: s?.kind === 'web' || s?.kind === 'erp' || s?.kind === 'document' ? s.kind : 'web',
            title: typeof s?.title === 'string' ? s.title : '',
            url: typeof s?.url === 'string' ? s.url : undefined,
            recordId: typeof s?.recordId === 'string' ? s.recordId : undefined,
            snippet: typeof s?.snippet === 'string' ? s.snippet : '',
            accessedAt: typeof s?.accessedAt === 'string' ? s.accessedAt : '',
            publishedAt: typeof s?.publishedAt === 'string' ? s.publishedAt : undefined,
          })),
        }
      : {}),
    ...(Array.isArray(o['contradictions'])
      ? {
          contradictions: (o['contradictions'] as unknown[]).filter((c): c is string => typeof c === 'string'),
        }
      : {}),
  };
}

/** `GET /runs/:runId`. */
export function parseRunStatus(value: unknown): AiRunStatus {
  const o = rec(value, 'run');
  return {
    runId: str(o['runId'], 'run.runId'),
    turnId: strOrNull(o['turnId'], 'run.turnId'),
    quoteId: strOrNull(o['quoteId'], 'run.quoteId'),
    state: oneOf(o['state'], RUN_STATES, 'run.state'),
    stateLabel: str(o['stateLabel'], 'run.stateLabel'),
    execution: parseExecution(o['execution'], 'run.execution'),
    settlement: parseSettlement(o['settlement'], 'run.settlement'),
    usage: parseTokens(o['usage'], 'run.usage'),
    amountToman: digitsOrNull(o['amountToman'], 'run.amountToman'),
    amountLabel: str(o['amountLabel'], 'run.amountLabel'),
    featureCode: str(o['featureCode'], 'run.featureCode'),
  };
}

/** `GET /runs/idempotency/:idempotencyKey`. */
export function parseRunRecovery(value: unknown): AiRunRecovery {
  const o = rec(value, 'recovery');
  return {
    idempotencyKey: str(o['idempotencyKey'], 'recovery.idempotencyKey'),
    runId: strOrNull(o['runId'], 'recovery.runId'),
    turnId: strOrNull(o['turnId'], 'recovery.turnId'),
    state: oneOf(o['state'], RUN_STATES, 'recovery.state'),
    stateLabel: str(o['stateLabel'], 'recovery.stateLabel'),
    execution: parseExecution(o['execution'], 'recovery.execution'),
    settlement: parseSettlement(o['settlement'], 'recovery.settlement'),
    usage: parseTokens(o['usage'], 'recovery.usage'),
  };
}

function parseUsageRow(entry: unknown, path: string): AiUsageRow {
  const o = rec(entry, path);
  return {
    turnId: str(o['turnId'], `${path}.turnId`),
    runId: strOrNull(o['runId'], `${path}.runId`),
    quoteId: strOrNull(o['quoteId'], `${path}.quoteId`),
    correlationId: strOrNull(o['correlationId'], `${path}.correlationId`),
    createdAt: str(o['createdAt'], `${path}.createdAt`),
    execution: parseExecution(o['execution'], `${path}.execution`),
    settlement: parseSettlement(o['settlement'], `${path}.settlement`),
    state: oneOf(o['state'], RUN_STATES, `${path}.state`),
    stateLabel: str(o['stateLabel'], `${path}.stateLabel`),
    usage: parseTokens(o['usage'], `${path}.usage`),
    charge: o['charge'] === null ? null : parseMoney(o['charge'], `${path}.charge`),
    amountToman: digitsOrNull(o['amountToman'], `${path}.amountToman`),
    amountLabel: str(o['amountLabel'], `${path}.amountLabel`),
  };
}

const MAX_USAGE_ITEMS = 50;

/**
 * `GET /usage` is exactly `{ scope, items }` (AI-CONNECT-001.2). A top-level
 * array, a wrapper under another name, a scope that differs from the request
 * or more than 50 rows is an invalid response.
 */
export function parseUsage(value: unknown, requested: 'own' | 'all'): AiUsageResponse {
  const o = rec(value, 'usage');
  const keys = Object.keys(o).sort().join(',');
  if (keys !== 'items,scope') fail('usage');
  const scope = oneOf(o['scope'], ['own', 'all'] as const, 'usage.scope');
  if (scope !== requested) fail('usage.scope');
  const items = arr(o['items'], 'usage.items');
  if (items.length > MAX_USAGE_ITEMS) fail('usage.items');
  return { scope, items: items.map((entry, i) => parseUsageRow(entry, `usage.items[${i}]`)) };
}

/** `PUT /activation`, `/settings` and `/budgets` answer `{ saved: true }` (backend controller). */
export function parseSaved(value: unknown): void {
  const o = rec(value, 'saved');
  if (o['saved'] !== true) fail('saved');
}

export function parseWalletPackages(value: unknown): AiWalletPackagesResponse {
  const o = rec(value, 'walletPackages');
  const packagesRaw = arr(o['packages'], 'walletPackages.packages');
  const boundsRaw = rec(o['bounds'], 'walletPackages.bounds');
  const pricingMode =
    o['pricingMode'] === undefined
      ? undefined
      : oneOf(o['pricingMode'], ['raw_tokens', 'cost_plus'] as const, 'walletPackages.pricingMode');

  const packages: AiCreditPackage[] = packagesRaw.map((pkgRaw, i) => {
    const p = rec(pkgRaw, `walletPackages.packages[${i}]`);
    return {
      id: str(p['id'], `walletPackages.packages[${i}].id`),
      title: str(p['title'], `walletPackages.packages[${i}].title`),
      amountToman: digits(p['amountToman'], `walletPackages.packages[${i}].amountToman`),
      amountRial: digits(p['amountRial'], `walletPackages.packages[${i}].amountRial`),
      amountMicroCredits: digits(
        p['amountMicroCredits'],
        `walletPackages.packages[${i}].amountMicroCredits`,
      ),
      description: str(p['description'], `walletPackages.packages[${i}].description`),
      badge: p['badge'] === null ? null : str(p['badge'], `walletPackages.packages[${i}].badge`),
      ...(p['tokenCount'] === undefined
        ? {}
        : {
            tokenCount: digitsOrNull(p['tokenCount'], `walletPackages.packages[${i}].tokenCount`),
          }),
    };
  });

  const bounds = {
    minToman: digits(boundsRaw['minToman'], 'walletPackages.bounds.minToman'),
    maxToman: digits(boundsRaw['maxToman'], 'walletPackages.bounds.maxToman'),
    conversionRate: digits(boundsRaw['conversionRate'], 'walletPackages.bounds.conversionRate'),
  };

  if (pricingMode === 'raw_tokens') {
    const peg = BigInt(bounds.conversionRate);
    if (peg <= 0n || peg % 5n !== 0n) fail('walletPackages.precision');
    // ADR-021: a package has its own fixed price and quota, so the quota is not derived from the
    // price. It must still be exact: whole tokens, Rial = 10 × Toman, credit = tokens × unit.
    for (const pkg of packages) {
      if (
        pkg.tokenCount === undefined ||
        pkg.tokenCount === null ||
        BigInt(pkg.tokenCount) <= 0n ||
        BigInt(pkg.amountRial) !== BigInt(pkg.amountToman) * 10n ||
        BigInt(pkg.amountMicroCredits) !== BigInt(pkg.tokenCount) * (peg / 5n)
      )
        fail('walletPackages.rawQuota');
    }
  } else if (packages.some((pkg) => pkg.tokenCount !== undefined && pkg.tokenCount !== null)) {
    fail('walletPackages.rawQuotaWithoutPricing');
  }
  return {
    packages,
    bounds,
    ...(pricingMode === undefined ? {} : { pricingMode }),
    ...(typeof o['catalogVersion'] === 'number' ? { catalogVersion: o['catalogVersion'] } : {}),
    ...(typeof o['customAmountAllowed'] === 'boolean'
      ? { customAmountAllowed: o['customAmountAllowed'] }
      : {}),
  };
}

export function parseTopupCheckout(value: unknown): AiTopupCheckoutResponse {
  const o = rec(value, 'topupCheckout');
  return {
    orderId: str(o['orderId'], 'topupCheckout.orderId'),
    authority: str(o['authority'], 'topupCheckout.authority'),
    paymentUrl: str(o['paymentUrl'], 'topupCheckout.paymentUrl'),
    amountToman: digits(o['amountToman'], 'topupCheckout.amountToman'),
    amountRial: digits(o['amountRial'], 'topupCheckout.amountRial'),
    amountMicroCredits: digits(o['amountMicroCredits'], 'topupCheckout.amountMicroCredits'),
    conversionRate: digits(o['conversionRate'], 'topupCheckout.conversionRate'),
  };
}

export function parseTopupVerify(value: unknown): AiTopupVerifyResponse {
  const o = rec(value, 'topupVerify');
  const status = oneOf(
    o['status'],
    ['paid', 'failed', 'cancelled', 'pending'] as const,
    'topupVerify.status',
  );
  const refId = strOrNull(o['refId'], 'topupVerify.refId');
  const amountToman = digitsOrNull(o['amountToman'], 'topupVerify.amountToman');
  const amountMicroCredits = digitsOrNull(
    o['amountMicroCredits'],
    'topupVerify.amountMicroCredits',
  );
  if (
    status === 'paid' &&
    (!refId ||
      amountToman === null ||
      amountMicroCredits === null ||
      BigInt(amountToman) <= 0n ||
      BigInt(amountMicroCredits) <= 0n)
  )
    fail('topupVerify.paidReceipt');
  return {
    orderId: str(o['orderId'], 'topupVerify.orderId'),
    status,
    message: str(o['message'], 'topupVerify.message'),
    refId,
    amountToman,
    amountMicroCredits,
    tokenQuota: digitsOrNull(o['tokenQuota'] ?? null, 'topupVerify.tokenQuota'),
    alreadyVerified: bool(o['alreadyVerified'], 'topupVerify.alreadyVerified'),
  };
}

export function parseTopupOrders(value: unknown): readonly AiTopupOrder[] {
  const o = rec(value, 'topupOrders');
  const ordersRaw = arr(o['orders'], 'topupOrders.orders');
  return ordersRaw.map((orderRaw, i) => {
    const ord = rec(orderRaw, `topupOrders.orders[${i}]`);
    return {
      orderId: str(ord['orderId'], `topupOrders.orders[${i}].orderId`),
      amountToman: digits(ord['amountToman'], `topupOrders.orders[${i}].amountToman`),
      amountRial: digits(ord['amountRial'], `topupOrders.orders[${i}].amountRial`),
      amountMicroCredits: digits(
        ord['amountMicroCredits'],
        `topupOrders.orders[${i}].amountMicroCredits`,
      ),
      conversionRate: digits(ord['conversionRate'], `topupOrders.orders[${i}].conversionRate`),
      packageId: strOrNull(ord['packageId'] ?? null, `topupOrders.orders[${i}].packageId`),
      tokenQuota: digitsOrNull(ord['tokenQuota'] ?? null, `topupOrders.orders[${i}].tokenQuota`),
      authority: strOrNull(ord['authority'], `topupOrders.orders[${i}].authority`),
      status: oneOf(
        ord['status'],
        ['pending', 'paid', 'failed', 'cancelled'] as const,
        `topupOrders.orders[${i}].status`,
      ),
      refId: strOrNull(ord['refId'], `topupOrders.orders[${i}].refId`),
      cardPan: strOrNull(ord['cardPan'], `topupOrders.orders[${i}].cardPan`),
      createdAt: str(ord['createdAt'], `topupOrders.orders[${i}].createdAt`),
      settledAt: strOrNull(ord['settledAt'], `topupOrders.orders[${i}].settledAt`),
      errorMessage: strOrNull(ord['errorMessage'], `topupOrders.orders[${i}].errorMessage`),
    };
  });
}

export function parseWelcomeCredit(value: unknown): AiWelcomeCredit {
  const o = rec(value, 'welcomeCredit');
  const status = oneOf(
    o['status'],
    ['granted', 'already_granted', 'disabled', 'configuration_required'] as const,
    'welcomeCredit.status',
  );
  const program = str(o['program'], 'welcomeCredit.program');
  if (program !== 'tenant-welcome-credit') fail('welcomeCredit.program');
  const granted = status === 'granted' || status === 'already_granted';
  const amountToman = digitsOrNull(o['amountToman'], 'welcomeCredit.amountToman');
  const amountRial = digitsOrNull(o['amountRial'], 'welcomeCredit.amountRial');
  const amountMicroCredits = digitsOrNull(
    o['amountMicroCredits'],
    'welcomeCredit.amountMicroCredits',
  );
  const conversionRate = digitsOrNull(o['conversionRate'], 'welcomeCredit.conversionRate');
  const version = o['conversionVersion'];
  const operationId = strOrNull(o['operationId'], 'welcomeCredit.operationId');
  const bucketId = strOrNull(o['bucketId'], 'welcomeCredit.bucketId');
  const grantedAt = strOrNull(o['grantedAt'], 'welcomeCredit.grantedAt');
  if (granted) {
    if (
      amountToman === null ||
      amountRial === null ||
      amountMicroCredits === null ||
      conversionRate === null ||
      typeof version !== 'number' ||
      !Number.isSafeInteger(version) ||
      version < 1 ||
      !operationId ||
      !bucketId ||
      !grantedAt ||
      !Number.isFinite(Date.parse(grantedAt))
    )
      fail('welcomeCredit.receipt');
    if (
      BigInt(amountToman) <= 0n ||
      BigInt(conversionRate) <= 0n ||
      BigInt(amountToman) * 10n !== BigInt(amountRial) ||
      BigInt(amountToman) * BigInt(conversionRate) !== BigInt(amountMicroCredits)
    )
      fail('welcomeCredit.amount');
  } else if (
    [
      amountToman,
      amountRial,
      amountMicroCredits,
      conversionRate,
      version,
      operationId,
      bucketId,
      grantedAt,
    ].some((v) => v !== null)
  ) {
    fail('welcomeCredit.uncommitted');
  }
  return {
    status,
    program,
    amountToman,
    amountRial,
    amountMicroCredits,
    conversionRate,
    conversionVersion: version as number | null,
    operationId,
    bucketId,
    grantedAt,
  };
}
